import React, { useEffect, useState, useMemo, useRef } from "react";
import { Input, Segmented, Select } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ServicesOpTable from "./ServicesOpTable";
import AssignedOpServicesTable from "./AssignedOpServicesTable";
import { getCategoryApi } from "../../../../services/service.services";
import { debounce } from "../../../../helpers/utility";
import { UiButton, UiTag } from "../../../ui";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import useQuery from "../../../../hooks/useQuery";
import {
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import styles from "./ServicesOpTabs.module.css";

const servicesKeys = [
  { id: "all_services", name: "All Services" },
  { id: "assigned_services", name: "Assigned Services" },
];

const ServicesOpTabs = ({ patientData }) => {
  const navigate = useNavigate();
  const params = useQuery();
  const urlSegment = params?.view || "all_services";
  const [segmentView, setSegmentView] = useState(urlSegment);
  const [mountedViews, setMountedViews] = useState(() => ({
    all_services: urlSegment === "all_services",
    assigned_services: urlSegment === "assigned_services",
  }));
  const searchQuery = parseSearchParam(params);
  const categoryFilter = params?.category ? Number(params.category) : null;
  const [categories, setCategories] = useState([]);
  const [serviceCount, setServiceCount] = useState(0);
  const panelBodyRef = useRef(null);

  useEffect(() => {
    setSegmentView(urlSegment);
    setMountedViews((prev) => ({
      ...prev,
      [urlSegment]: true,
    }));
  }, [urlSegment]);

  useEffect(() => {
    getCategoryApi()
      .then((res) => setCategories(res?.data ?? []))
      .catch((err) => console.log(err));
  }, []);

  // The input is driven by local state, not the URL. Binding it straight to the
  // debounced URL value discards every keystroke typed inside the debounce
  // window, so only a single character ever survives.
  const [searchInput, setSearchInput] = useState(searchQuery || "");

  // Re-sync when the URL changes from elsewhere (clear filters, segment switch,
  // browser back/forward).
  useEffect(() => {
    setSearchInput(searchQuery || "");
  }, [searchQuery]);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        updateUrlSearchParams(navigate, { search: value || null, page: 1 });
      }, 300),
    [navigate]
  );

  const onSearch = (e) => {
    const value = e?.target?.value ?? "";
    setSearchInput(value);
    debouncedSetSearchQuery(value);
  };

  const onSegmentChange = (value) => {
    if (value === segmentView) return;
    setSegmentView(value);
    setMountedViews((prev) => ({ ...prev, [value]: true }));
    updateUrlSearchParams(navigate, {
      view: value,
      search: null,
      category: null,
      page: 1,
    });
    setServiceCount(0);
  };

  useEffect(() => {
    const body = panelBodyRef.current;
    if (!body) return;
    body.scrollTop = 0;
    body.querySelectorAll(
      ".ui-table-fill-body, .ant-table-body, .patient-list-scroll"
    ).forEach((el) => {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    });
  }, [segmentView]);

  const clearFilters = () => {
    updateUrlSearchParams(navigate, { search: null, category: null, page: 1 });
  };

  const hasActiveFilters = Boolean(searchInput || categoryFilter);

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <Segmented
          size="large"
          className={styles.segmented}
          options={servicesKeys.map((i) => ({ label: i?.name, value: i?.id }))}
          value={segmentView}
          onChange={onSegmentChange}
        />
      </div>

      <div className={styles.panelToolbar}>
        <div className={styles.searchGroup}>
          <Input
            className={styles.searchInput}
            size="middle"
            allowClear
            prefix={<SearchOutlined className={styles.searchIcon} />}
            placeholder="Search for services"
            value={searchInput}
            onChange={onSearch}
          />
          <UiTag tone="info" size="sm" className={styles.searchCount}>
            {serviceCount} services
          </UiTag>
        </div>

        <div className={styles.filtersGroup}>
          {segmentView === "all_services" ? (
            <>
              <Select
                className={`${inputStyles.mdSelect} ${styles.filterControl} ${styles.filterSelect}`}
                size="middle"
                placeholder="All categories"
                value={categoryFilter}
                onChange={(value) =>
                  updateUrlSearchParams(navigate, {
                    category: value,
                    page: 1,
                  })
                }
                allowClear
                popupMatchSelectWidth={false}
              >
                {categories.map((category) => (
                  <Select.Option key={category.id} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
              <UiButton
                variant="secondary"
                size="md"
                className={styles.clearBtn}
                icon={<CloseOutlined />}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </UiButton>
            </>
          ) : null}
        </div>
      </div>

      <div className={styles.servicesPanel}>
        <div className={styles.panelBody} ref={panelBodyRef}>
          {mountedViews.all_services && (
            <div
              className={
                segmentView !== "all_services" ? styles.hiddenPanel : undefined
              }
            >
              <ServicesOpTable
                admissionId={patientData}
                searchQuery={searchQuery}
                categoryFilter={categoryFilter}
                categories={categories}
                onCountChange={setServiceCount}
              />
            </div>
          )}
          {mountedViews.assigned_services && (
            <div
              className={
                segmentView !== "assigned_services"
                  ? styles.hiddenPanel
                  : undefined
              }
            >
              <AssignedOpServicesTable
                admissionId={patientData}
                searchQuery={searchQuery}
                onCountChange={setServiceCount}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesOpTabs;
